import React, { FC, ReactElement, ReactNode, useMemo, useEffect, useState, useCallback } from 'react';
import { DisciplineLiveStatus, CounterType, DisciplinesType } from '@om-fe/models';
import LiveMatchListSelector from '../../components/match-list/containers/live-match-list/live-match-list-selector';
import getConfig, { MarketsHeaderOptions, OMFE2ConfigModel } from '@om-fe/core/utils/config';
import SimpleSearchTrigger from '@om-fe/core/components/search/simple-search-trigger';
import { SearchPageComponent } from '@om-fe/core/pages/theme1/search-page';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import Anchor from '@om-fe/core/components/anchor/anchor';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import { bem } from '@om-fe/core/components/live-event/sidebar/sports-sidebar/live-event-sidebar';
import { LayoutType } from '@om-fe/widgets/dist/components/nav/nav.constants';
import { SportsNavModel, SportsNavStoreData, UseSportsNavStore, useSportsNavList } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.hooks';
import { SportRouteInComponentProps } from '@om-fe/widgets/dist/common/routing/routing.constants';
import { SportsNavType } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.constants';
import { Spinner } from '@om-fe/widgets/dist/components/spinner/spinner';
import { SportsNav } from '@om-fe/widgets';
import FavoriteMatchList from '../../components/match-list/containers/favorite-match-list/favorite-match-list';
import FavoriteMixedMatchList from '../match-list/containers/favorite-match-list-v2/favorite-mixed-match-list';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import MatchListAll from '../match-list-all/match-list-all';
import { DisciplineId } from './match-content-tabs.constants';
import { PlatformTypes, isBrowser } from '@om-fe/utils';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { useReady } from '@om-fe/store';
import LocationNav, { LocationNavItemRouteParamsMap } from '@om-fe/core/components/nav-secondary/location-nav/location-nav';
import CategoryNav, { CategoryNavItemRouteParamsMap } from '@om-fe/core/components/nav-secondary/category-nav/category-nav';
import { getMainMarketsLimit, getMainMarketsLimitByWidth } from '@om-fe/core/components/match-list/utils/match-list.utils';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { navBem } from '@om-fe/widgets/dist/components/nav/nav.utils';
import UserBehaviorTrackingContext from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking-context';
import { UserBehaviorTrackingLocation } from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking.utils';
import { RouteComponentProps } from 'react-router';
import { MAIN_MARKET_MIN_LIMIT } from '@om-fe/core/components/match-list/utils/match-list.constants';
import ParentNav from '@om-fe/core/components/nav-secondary/parent-nav/parent-nav';
import { ParentNavItemRouteParamsMap } from '@om-fe/core/components/nav-secondary/parent-nav/parent-nav-item';

interface LiveMatchesContentPublicProps extends SportRouteInComponentProps {
    readonly useStore: UseSportsNavStore;
    readonly sportId?: string;
    readonly sportName?: string;
    readonly genericId?: string;
    readonly hasSearch?: boolean;
    readonly matchId?: string;
}

interface LiveMatchesContentPropsStoreProps {
    readonly userID?: number;
    readonly deviceType?: PlatformTypes;
    readonly breakpoints: Breakpoints;
    readonly isAuthenForTopic?: boolean;
}

type LiveMatchesContentProps = LiveMatchesContentPublicProps & LiveMatchesContentPropsStoreProps & RouteComponentProps;

function LiveMatchesContentComponent(
    {
        hasSearch = false,
        deviceType,
        userID,
        matchId,
        sportId,
        sportName,
        sportRoute,
        genericId,
        onRouteChange,
        useStore,
        breakpoints,
        location,
        isAuthenForTopic,
    }: LiveMatchesContentProps,
): ReactElement {
    const { gettext } = useI18NContext();
    const list: string[] = useSportsNavList(useStore);
    const ready: boolean = useReady(useStore);
    const navItem: SportsNavModel | undefined = useStore((state: SportsNavStoreData): SportsNavModel | undefined => {
        return sportId ? state.items[sportId] : undefined;
    });
    const expandedDiscipline = useMemo(() => {
        return list.find(id => id !== DisciplineId.AllSports && id !== DisciplineId.Favorites);
    }, [list]);
    const [prevSportId, setPrevSportId] = useState<string | undefined>(sportId);
    const [sportChildId, setSportChildId] = useState<string | undefined>(undefined);
    const [sportChildName, setSportChildName] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (sportId !== prevSportId) {
            setPrevSportId(sportId);
        }
    }, [prevSportId, sportId]);

    const onEsportsLoaded = useCallback((childId: string, childName: string) => {
        if (sportId === DisciplinesType.ElectronicSports && childId && childName) {
            setSportChildId(childId);
            setSportChildName(childName);
        }
    }, [sportId, setSportChildId, setSportChildName]);

    const config: OMFE2ConfigModel = getConfig();
    const mainMarketsLimit: number = config.mainMarketsLimit?.responsive
        ? config.mainMarketsLimit?.byWidth && (isBrowser() && window)
            ? getMainMarketsLimitByWidth(window.innerWidth, config.mainMarketsLimit?.breakpointsByWidth)
            : getMainMarketsLimit(breakpoints)
        : MAIN_MARKET_MIN_LIMIT;
    const matchListLayout = config.matchListConfig?.commonConfig?.matchListItemLayout;
    const eventsNumber: number | undefined = config.matchListConfig?.liveEvent?.eventsNumber;

    if (!ready) {
        return (
            <Spinner />
        );
    }

    let content: ReactNode;
    let contentNav: ReactNode;
    const configGeneral = getConfig() as OMFE2ConfigModel & { readonly marketsHeaderOptions? : MarketsHeaderOptions };
    const marketSelector: boolean = configGeneral?.matchListConfig?.matchListSelector?.enabled ?? false;
    const marketGroupsOverviewNumber: number | undefined = configGeneral?.matchListConfig?.matchListSelector?.numberOfList;
    const showMarketSelectorHeader: boolean = config.matchListConfig?.matchListSelector?.showHeader ?? true;
    const hideAllforEsportsParent: boolean = true;
    const filterLocationId = ((!navItem || !navItem.showEventCategory) && genericId && genericId != '0' ? true : false);

    if (sportId === DisciplineId.AllSports) {
        content = (
            <MatchListAll expandedDisciplines={[expandedDiscipline]} />
        );
    } else if (sportId === DisciplineId.Favorites) {
        content = config.favoriteV2
            ? (
                <FavoriteMixedMatchList
                    activeMatchId={matchId || null}
                    userID={userID}
                    instanceId={`LiveEventFavorites`}
                    liveStatus={DisciplineLiveStatus.LIVE}
                    limitNumberOfEvents={eventsNumber}
                    extraClassName={bem('MatchList')}
                    routeName={RouteNames.liveEvent}
                    showMixedMatchListHeader
                    itemLayout={matchListLayout}
                    marketsHeaderOptions={configGeneral.marketsHeaderOptions}
                    mainMarketsLimit={mainMarketsLimit}
                    showMatchListHeader={false}
                    showMarketInMatchListGroupHeader={true}
                />
            )
            : (
                <FavoriteMatchList
                    activeMatchId={matchId || null}
                    userID={userID}
                    instanceId={`LiveEventFavorites`}
                    liveStatus={DisciplineLiveStatus.LIVE}
                    limitNumberOfEvents={eventsNumber}
                    extraClassName={bem('MatchList')}
                    routeName={RouteNames.liveEvent}
                    showMixedMatchListHeader
                    itemLayout={matchListLayout}
                    marketsHeaderOptions={configGeneral.marketsHeaderOptions}
                    mainMarketsLimit={mainMarketsLimit}
                    showMatchListHeader={false}
                    showMarketInMatchListGroupHeader={true}
                />
            )
        ;
    } else {
        const filterEventCategoryId = (navItem && navItem.showEventCategory && genericId && genericId != '0' ? true : false);

        content = prevSportId !== sportId ? (
            <Spinner />
        ) : (
            <LiveMatchListSelector
                itemLayout={matchListLayout}
                disciplineId={sportId}
                instanceId={`SportsNavDiscipline-LiveUpcomingTabs-${DisciplineLiveStatus.LIVE}`}
                limitNumberOfEvents={eventsNumber}
                liveStatus={DisciplineLiveStatus.LIVE}
                mainMarketsLimit={mainMarketsLimit}
                marketsHeaderOptions={configGeneral.marketsHeaderOptions}
                eventCategoryId={filterEventCategoryId ? genericId : undefined}
                locationId={(sportId === DisciplinesType.ElectronicSports && genericId === '0' ? sportChildId : undefined) || (filterLocationId ? genericId : undefined)}
                marketGroupsOverviewNumber={marketGroupsOverviewNumber}
                hasMarketSelector={marketSelector}
                showMatchListHeader={false}
                showMarketInMatchListGroupHeader={true}
                deviceType={deviceType}
                showMarketSelectorHeader={showMarketSelectorHeader}
                isAuthenForTopic={isAuthenForTopic}
            />
        );
    }

    if (navItem && navItem.id !== 'favorites') {
        if (navItem.showEventCategory) {
            const routeParamsMap: CategoryNavItemRouteParamsMap = {
                eventCategoryName: 'liveLocationName',
                eventCategoryId: 'liveLocationId',
                disciplineId: 'liveDisciplineId',
                disciplineName: 'liveDisciplineName',
            };

            contentNav = (
                <div className={navBem()}>
                    <CategoryNav
                        disciplineId={sportId}
                        disciplineName={sportName}
                        instanceId={`CategoryNav-LiveUpcomingTabs-${DisciplineLiveStatus.LIVE}`}
                        routeName={RouteNames.liveSports}
                        routeParamsMap={routeParamsMap}
                        counterType={CounterType.numberOfLiveEvents}
                        hasArrows={deviceType === 'desktop'}
                        liveStatus={DisciplineLiveStatus.LIVE}
                    />
                </div>
            );
        } else if (navItem.displayChildren) {
            const routeParamsMap: ParentNavItemRouteParamsMap = {
                sportChildName: 'liveLocationName',
                sportChildId: 'liveLocationId',
                disciplineId: 'liveDisciplineId',
                disciplineName: 'liveDisciplineName',
            };

            contentNav = (
                <div className={navBem({
                    modifier: {
                        [`SportId-${sportId}`]: true,
                        ParentNav: true
                    }
                })}>
                    <ParentNav
                        counterType={CounterType.numberOfLiveEvents}
                        disciplineId={sportId}
                        disciplineName={sportName}
                        liveStatus={DisciplineLiveStatus.LIVE}
                        routeParamsMap={routeParamsMap}
                        routeName={RouteNames.liveSports}
                        instanceId={`ParentNav-LiveUpcomingTabs-${DisciplineLiveStatus.LIVE}`}
                        genericId={(sportId === DisciplinesType.ElectronicSports && genericId === '0' ? sportChildId : undefined) || (filterLocationId ? genericId : undefined)}
                        hasArrows={deviceType === 'desktop'}
                        hideAll={hideAllforEsportsParent}
                        sportChild={onEsportsLoaded}
                    />
                </div>
            );
        } else {
            const routeParamsMap: LocationNavItemRouteParamsMap = {
                sportLocationName: 'liveLocationName',
                sportLocationId: 'liveLocationId',
                disciplineId: 'liveDisciplineId',
                disciplineName: 'liveDisciplineName',
            };

            contentNav = (
                <div className={navBem()}>
                    <LocationNav
                        counterType={CounterType.numberOfLiveEvents}
                        disciplineId={sportId}
                        disciplineName={sportName}
                        liveStatus={DisciplineLiveStatus.LIVE}
                        routeName={RouteNames.liveSports}
                        routeParamsMap={routeParamsMap}
                        instanceId={`LocationNav-LiveUpcomingTabs-${DisciplineLiveStatus.LIVE}`}
                        hasArrows={deviceType === 'desktop'}
                    />
                </div>
            );
        }
    }

    return (
        <UserBehaviorTrackingContext.Provider value={{ userBehaviorTrackingPage: RouteNames.liveSports, userBehaviorTrackingLocation: UserBehaviorTrackingLocation.LiveMatchList }}>
            <div className={'NavWrapper'}>
                {config.showLiveHomeButton && (
                    <Anchor
                        className={'LiveHomeButton'}
                        routeName={RouteNames.home}
                        title={gettext('Home')}
                    >
                        <GeneralIcon name={'arrow-big-back'} />
                        <span>{gettext('Home')}</span>
                    </Anchor>
                )}
                <SportsNav
                    activeItemId={sportId}
                    sportRoute={sportRoute}
                    onRouteChange={onRouteChange}
                    useStore={useStore}
                    className={`${SportsNavType.Live}SportsNav`}
                    showIcon
                    hasArrows={deviceType === 'desktop'}
                    layoutType={LayoutType.Main}
                    searchComponent={(
                        hasSearch && !config.enabledTopMenu
                            ? <SimpleSearchTrigger
                                defaultSearchMap={SearchPageComponent.defaultSearchMap}
                                searchRouteName={RouteNames.search}
                                searchPlaceholder={gettext('Search')}
                              />
                            : undefined
                    )}
                    disableEsportNavItem={config.layout?.disableEsportNavItem}
                    hideAllforEsportsParent={hideAllforEsportsParent}
                    sportChildId={sportChildId}
                    sportChildName={sportChildName}
                    disableNavItemById={[DisciplinesType.Olympics]}
                />
            </div>
            {contentNav}
            {content}
        </UserBehaviorTrackingContext.Provider>
    );
}

export const LiveMatchesContent: FC<LiveMatchesContentPublicProps> = connect(
    (state: AppState): LiveMatchesContentPropsStoreProps => {
        const { enabledTopicDelay } = getConfig();
        const isAuthenForTopic: boolean = !!(enabledTopicDelay && state.session?.info?.isAuthenticated);

        return {
            userID: state.session.info?.userID,
            deviceType: state.deviceInfo?.platform.type,
            breakpoints: state.breakpoints,
            isAuthenForTopic,
        };
    },
)(LiveMatchesContentComponent);
